import Field from './Field.jsx';
import DataTable from './DataTable.jsx';
import NoneMarkers from './NoneMarkers.jsx';
import FamilySection from './FamilySection.jsx';
import EmploymentSection from './EmploymentSection.jsx';
import LiabilitiesSection from './LiabilitiesSection.jsx';
import IncomeSection from './IncomeSection.jsx';
import AssetsSection from './AssetsSection.jsx';
import TaxesExpensesSection from './TaxesExpensesSection.jsx';
import { useStore } from '../store/StoreContext.jsx';
import { isHouseholdOwned, HOUSEHOLD_LOCK_HINT } from '../schema/householdOwned.js';
import { sectionProgress } from '../store/completion.js';
import { TONE_BY_SECTION } from '../schema/pqTabs.js';

const CUSTOM_RENDERERS = {
  family: FamilySection,
  employment: EmploymentSection,
  liabilities: LiabilitiesSection,
  income: IncomeSection,
  assets: AssetsSection,
  taxesExpenses: TaxesExpensesSection,
};

function isFilled(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.some(isFilled);
  if (typeof v === 'object') return Object.values(v).some(isFilled);
  return true;
}

function SectionShell({ section, num, action, count, children }) {
  const { data } = useStore();
  const tone = TONE_BY_SECTION[section.id] || 'neutral';
  // Same per-section progress the side nav shows, rendered in the section
  // header so custom-rendered sections (Assets, Income, ...) carry the same
  // signal as schema-rendered ones instead of showing nothing.
  const pct = sectionProgress(section, data);
  return (
    <section
      id={`sec-${section.id}`}
      className={`section section-tone-${tone}`}
    >
      <header className="section-head">
        <div className="section-head-l">
          <span className="section-num">{num}</span>
          <h2 className="section-title">{section.title}</h2>
        </div>
        <div className="section-meta">
          {count != null && <span>{count}</span>}
          <div
            className={
              'section-progress section-progress-mini' +
              (pct >= 100 ? ' is-complete' : '')
            }
          >
            <div className="section-progress-track">
              <div
                className="section-progress-fill"
                style={{ width: pct + '%' }}
              />
            </div>
            <div className="section-progress-label">{pct}%</div>
          </div>
          {action}
        </div>
      </header>
      <div className="section-body">{children}</div>
    </section>
  );
}

export default function FormSection({ section, num }) {
  const { data, update, householdMode } = useStore();
  const sectionData = data[section.id] || {};
  const flags = data._flags || {};

  const Custom = CUSTOM_RENDERERS[section.id];
  if (Custom) return <Custom section={section} num={num} SectionShell={SectionShell} />;

  if (section.customRenderer) {
    return (
      <SectionShell section={section} num={num}>
        <p className="section-note">
          (Custom-rendered section — not yet ported. Legacy renderer:{' '}
          <code>{section.customRenderer}</code>)
        </p>
      </SectionShell>
    );
  }

  const visibleFields = (section.fields || []).filter(
    f => !f.showIf || flags[f.showIf]
  );
  const filled = visibleFields.filter(f => isFilled(sectionData[f.key])).length;
  const count =
    visibleFields.length > 0
      ? `${filled} of ${visibleFields.length} fields`
      : null;

  return (
    <SectionShell section={section} num={num} count={count}>
      {section.note && <p className="section-note">{section.note}</p>}
      {visibleFields.length > 0 && (
        <div className="grid">
          {visibleFields.map(f => {
            // See FamilySection: on a household launch the household's own
            // details are shown, not edited here.
            const locked = householdMode && isHouseholdOwned(section.id, f.key);
            return (
              <Field
                key={f.key}
                field={locked ? { ...f, locked: true, lockHint: HOUSEHOLD_LOCK_HINT } : f}
                value={sectionData[f.key]}
                onChange={locked ? () => {} : val => update(`${section.id}.${f.key}`, val)}
              />
            );
          })}
        </div>
      )}
      {section.noneMarkers && (
        <NoneMarkers sectionId={section.id} config={section.noneMarkers} />
      )}
      {section.tables?.map(t => (
        <DataTable key={t.key} sectionId={section.id} tableDef={t} />
      ))}
    </SectionShell>
  );
}

export { SectionShell };
